import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  CreditCard, 
  Building2, 
  Globe, 
  Palette,
  Check,
  Gift,
  ArrowRight,
  Loader2,
  Shield,
  Zap,
  Star,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const productIcons = {
  brand: CreditCard,
  custom: Palette,
  enterprise: Building2,
  haiti_mondial: Globe
};

const productAccents = {
  brand: { bg: 'from-[#0A2540] to-[#0A2540]/80', light: 'bg-[#0A2540]/5', text: 'text-[#0A2540]', border: 'border-[#0A2540]/20', ring: 'ring-[#0A2540]' },
  custom: { bg: 'from-violet-600 to-purple-500', light: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200', ring: 'ring-violet-500' },
  enterprise: { bg: 'from-amber-500 to-orange-500', light: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', ring: 'ring-amber-500' },
  haiti_mondial: { bg: 'from-emerald-600 to-teal-500', light: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', ring: 'ring-emerald-500' }
};

const colorDots = {
  white: 'bg-white border border-slate-300',
  black: 'bg-slate-900',
  blue: 'bg-blue-600',
  pink: 'bg-pink-500',
  custom: 'bg-gradient-to-r from-red-400 via-yellow-400 to-blue-400'
};

export default function Products() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxImg, setLightboxImg] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API}/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00C48C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 overflow-hidden">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="relative max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#00C48C]/10 border border-[#00C48C]/30 text-sm text-[#00A67E] mb-8">
              <Star className="w-4 h-4 text-amber-500" />
              {t('productsCatalog.badge')}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-6" data-testid="products-page-title">
              {t('productsCatalog.title')}
            </h1>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              {t('productsCatalog.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="relative px-4 pb-32">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
          {products.map((product, idx) => {
            const productType = product.product_id.replace('card_', '');
            const Icon = productIcons[productType] || CreditCard;
            const accent = productAccents[productType] || productAccents.brand;
            // Use translated product data when available
            const td = t(`productData.${productType}`, { returnObjects: true });
            const productName = td?.name || product.name;
            const productDesc = td?.shortDescription || product.short_description || product.description;
            const productFeatures = Array.isArray(td?.features) ? td.features : (product.features || []);

            return (
              <motion.div
                key={product.product_id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="group relative bg-white border border-slate-200 rounded-3xl overflow-hidden hover:border-slate-300 hover:shadow-xl transition-all duration-500"
                data-testid={`product-card-${productType}`}
              >
                {/* Badge */}
                {product.badge && (
                  <div className="absolute top-4 right-4 z-10">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r ${accent.bg} text-white text-xs font-semibold shadow-lg`}>
                      {product.badge}
                    </span>
                  </div>
                )}
                {product.first_year_free && (
                  <div className="absolute top-4 left-4 z-10">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-500/90 text-white text-xs font-semibold shadow-lg">
                      <Gift className="w-3 h-3" />
                      {t('productsCatalog.firstYearFree')}
                    </span>
                  </div>
                )}

                <div className="p-8">
                  {/* Product Image — click to enlarge */}
                  <div className="flex justify-center mb-6">
                    <div
                      className="relative w-64 h-64 flex items-center justify-center cursor-pointer"
                      onClick={() => setLightboxImg(product.image)}
                      data-testid={`product-image-${productType}`}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br ${accent.bg} opacity-5 rounded-3xl blur-2xl group-hover:opacity-10 transition-opacity duration-500`} />
                      <img
                        src={product.image}
                        alt={product.name}
                        className="relative w-56 h-56 object-contain drop-shadow-2xl group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  </div>

                  {/* Icon + Name (clickable) */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${accent.bg} flex items-center justify-center shadow-lg`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2
                        className="font-heading text-xl font-bold text-slate-900 hover:text-[#00C48C] cursor-pointer transition-colors"
                        onClick={() => navigate(`/products/${productType}`)}
                        data-testid={`product-title-${productType}`}
                      >
                        {productName}
                      </h2>
                      <span className="text-xs text-slate-500 font-medium uppercase tracking-wider">
                        {product.subscription_label} {t('productsCatalog.subscription')}
                      </span>
                    </div>
                  </div>

                  {/* Short Description */}
                  <p className="text-sm text-slate-500 mb-5 leading-relaxed">
                    {productDesc}
                  </p>

                  {/* Colors */}
                  {product.colors && product.colors.length > 0 && (
                    <div className="flex items-center gap-2 mb-5">
                      <span className="text-xs text-slate-400 mr-1">{t('productsCatalog.colors')}:</span>
                      {product.colors.map(c => (
                        <div key={c} className={`w-5 h-5 rounded-full ${colorDots[c] || 'bg-gray-400'} shadow-sm`} title={c} />
                      ))}
                    </div>
                  )}

                  {/* Features (first 4) */}
                  {productFeatures.length > 0 && (
                    <ul className="space-y-2 mb-6">
                      {productFeatures.slice(0, 4).map((feat, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                          <Check className={`w-4 h-4 flex-shrink-0 ${accent.text}`} />
                          {feat}
                        </li>
                      ))}
                      {productFeatures.length > 4 && (
                        <li className="text-xs text-slate-400 pl-6">
                          +{productFeatures.length - 4} {t('productsCatalog.moreFeatures')}
                        </li>
                      )}
                    </ul>
                  )}

                  {/* Price + CTA */}
                  <div className="flex items-end justify-between pt-4 border-t border-slate-100">
                    <div>
                      {product.card_price > 0 ? (
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-bold text-slate-900">${product.card_price}</span>
                          <span className="text-sm text-slate-400">/{t('productsCatalog.perCard')}</span>
                        </div>
                      ) : (
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-bold text-emerald-600">{t('productsCatalog.free')}</span>
                          <span className="text-sm text-slate-400">{t('productsCatalog.cardIncluded')}</span>
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={() => navigate(`/products/${productType}`)}
                      className={`rounded-full px-6 bg-gradient-to-r ${accent.bg} text-white hover:opacity-90 shadow-lg transition-all duration-300`}
                      data-testid={`view-product-${productType}`}
                    >
                      {t('productsCatalog.viewProduct')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Trust Badges */}
      <section className="relative px-4 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-3 gap-6 text-center">
            {[
              { icon: Shield, label: t('productsCatalog.securePayment') },
              { icon: Zap, label: t('productsCatalog.instantActivation') },
              { icon: Star, label: t('productsCatalog.premiumQuality') }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className="flex flex-col items-center gap-2"
              >
                <div className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-slate-500" />
                </div>
                <span className="text-sm text-slate-500 font-medium">{item.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightboxImg(null)}
        >
          <img
            src={lightboxImg}
            alt="Product"
            className="max-w-full max-h-[85vh] object-contain drop-shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
          <button
            onClick={() => setLightboxImg(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}
